import React from "react";

/**
 * ReadinessBadge — Compact radial progress indicator for module readiness.
 * Shows how many records are "complete" (key fields populated) vs total.
 * 
 * Props:
 *   total        — Total number of records
 *   complete     — Number of records with key fields populated
 *   label        — Short label (e.g., "Assets Registered")
 *   target       — Optional soft target (shows "of X target" if provided)
 */
export default function ReadinessBadge({ total = 0, complete = 0, label = "Records", target, completeLabel = "fully documented" }) {
  const pct = total === 0 ? 0 : Math.round((complete / total) * 100);
  const displayPct = target ? Math.min(Math.round((total / target) * 100), 100) : pct;
  const r = 18;
  const circ = 2 * Math.PI * r;
  const offset = circ - (displayPct / 100) * circ;
  const color =
    displayPct >= 80 ? "#059669" :
    displayPct >= 40 ? "#d97706" :
    displayPct > 0 ? "#ea580c" : "#94a3b8";

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 bg-white/80 backdrop-blur border border-slate-200 rounded-xl shadow-sm"
      data-testid="readiness-badge"
    >
      <div className="relative w-11 h-11 flex-shrink-0">
        <svg viewBox="0 0 44 44" className="w-full h-full -rotate-90">
          <circle cx="22" cy="22" r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
          <circle
            cx="22" cy="22" r={r} fill="none"
            stroke={color} strokeWidth="4"
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <span
          className="absolute inset-0 flex items-center justify-center text-[11px] font-bold"
          style={{ color }}
        >
          {displayPct}%
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800 leading-tight truncate">
          {total} {label}
        </p>
        <p className="text-[11px] text-slate-500 leading-tight">
          {target
            ? `${total} of ${target} target`
            : total === 0
              ? "Get started by adding your first record"
              : `${complete} ${completeLabel}`
          }
        </p>
      </div>
    </div>
  );
}
